import React, { useEffect, useState } from 'react';
import { Drawer, Modal, Button, confirmAlert } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import {
  MdVerified, MdCameraAlt, MdEdit,
  MdEmail, MdPhone, MdBusiness, MdLocationOn, MdBadge,
  MdAccountCircle, MdCloudUpload, MdClose,
  MdDeleteOutline, MdVisibility, MdVisibilityOff, MdPerson,
} from 'react-icons/md';
import { getProfileUser } from 'redux/profileUserSlice';
import { changeAvatar, removeAvatar } from 'redux/authorization/userSlice';
// ── Tokens ────────────────────────────────────────────────────────────────────
import { INPUT_CLS as INP } from 'helpers/formStyles';
import MySignInActivity from './MySignInActivity';
const INP_RO = 'tw-w-full tw-border tw-border-slate-100 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-slate-400 tw-bg-slate-50 tw-outline-none tw-cursor-default';
const LBL    = 'tw-block tw-text-[11px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide tw-mb-1';

// ── AvatarDisplay ─────────────────────────────────────────────────────────────
const AvatarDisplay = ({ src, initials, className = '', imgClass = '' }) => (
  src ? (
    <img src={src} alt="avatar" className={`tw-rounded-xl tw-object-cover ${imgClass} ${className}`} />
  ) : (
    <div className={`tw-rounded-xl tw-bg-gradient-to-br tw-from-primary-500 tw-to-violet-600 tw-flex tw-items-center tw-justify-center tw-select-none ${className}`}>
      <span className="tw-font-bold tw-text-white">{initials}</span>
    </div>
  )
);
AvatarDisplay.propTypes = { src: PropTypes.string, initials: PropTypes.string, className: PropTypes.string, imgClass: PropTypes.string };

// ── InfoRow ───────────────────────────────────────────────────────────────────
const InfoRow = ({ icon: Icon, iconCls, label, value }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-py-3 tw-border-b tw-border-slate-50 last:tw-border-0">
    <div className={`tw-w-8 tw-h-8 tw-rounded tw-flex tw-items-center tw-justify-center tw-shrink-0 ${iconCls}`}>
      <Icon size={15} className="tw-text-white" />
    </div>
    <div className="tw-flex-1 tw-min-w-0">
      <p className="tw-text-[10px] tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wide tw-mb-0">{label}</p>
      <p className="tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-0 tw-truncate">{value || <span className="tw-text-slate-300">—</span>}</p>
    </div>
  </div>
);
InfoRow.propTypes = { icon: PropTypes.elementType, iconCls: PropTypes.string, label: PropTypes.string, value: PropTypes.string };

// ── PasswordField ─────────────────────────────────────────────────────────────
const PasswordField = ({ label, reg, error, show, onToggle }) => (
  <div>
    <label className={LBL}>{label}</label>
    <div className="tw-relative">
      <input type={show ? 'text' : 'password'} placeholder={label}
        className={`${INP} tw-pr-9`} {...reg} />
      <button type="button" onClick={onToggle}
        className="tw-absolute tw-right-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-slate-400 hover:tw-text-slate-600 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0">
        {show ? <MdVisibilityOff size={16} /> : <MdVisibility size={16} />}
      </button>
    </div>
    {error && <p className="tw-text-[11px] tw-text-red-500 tw-mt-0.5 tw-mb-0">{error.message}</p>}
  </div>
);
PasswordField.propTypes = { label: PropTypes.string, reg: PropTypes.object, error: PropTypes.object, show: PropTypes.bool, onToggle: PropTypes.func };

// ── AvatarModal ───────────────────────────────────────────────────────────────
const AvatarModal = ({ show, onHide, currentSrc, initials, onUpload, onRemove, uploading, removing }) => {
  const [selected, setSelected] = useState(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: 'image/jpeg, image/png, image/webp',
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    onDrop: (ok) => { if (ok[0]) setSelected({ file: ok[0], preview: URL.createObjectURL(ok[0]) }); },
    onDropRejected: () => toast.error('Max 5 MB · JPG / PNG / WEBP only.'),
  });

  const handleClose  = () => { setSelected(null); onHide(); };
  const handleUpload = async () => { if (!selected) return; await onUpload(selected.file); setSelected(null); };

  return (
    <Modal show={show} onHide={handleClose}
      title={<span className="tw-flex tw-items-center tw-gap-1.5"><MdCameraAlt size={16} />Change Avatar</span>}
      size="sm"
      footer={
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full">
          <div>
            {currentSrc && (
              <Button variant="danger" size="sm" onClick={onRemove} disabled={uploading || removing}>
                <MdDeleteOutline size={15} className="tw-mr-1" />{removing ? 'Removing…' : 'Remove photo'}
              </Button>
            )}
          </div>
          <div className="tw-flex tw-gap-2">
            <Button variant="outline" size="sm" onClick={handleClose} disabled={uploading || removing}>Cancel</Button>
            <Button size="sm" onClick={handleUpload} disabled={!selected || uploading || removing}>
              {uploading ? <><span className="tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-border-2 tw-border-white/40 tw-border-t-white tw-animate-spin" />Uploading…</> : 'Upload'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-1">
        {/* Before → After */}
        <div className="tw-flex tw-items-end tw-gap-5">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5">
            <AvatarDisplay src={currentSrc} initials={initials}
              className="tw-w-16 tw-h-16 tw-border-2 tw-border-white tw-shadow-sm" imgClass="tw-w-16 tw-h-16" />
            <span className="tw-text-[10px] tw-text-slate-400 tw-font-medium">Current</span>
          </div>
          {selected && <>
            <span className="tw-text-slate-300 tw-text-xl tw-pb-5">→</span>
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1.5">
              <img src={selected.preview} alt="new"
                className="tw-w-16 tw-h-16 tw-rounded-xl tw-object-cover tw-border-2 tw-border-primary-300 tw-shadow-sm" />
              <span className="tw-text-[10px] tw-text-primary-500 tw-font-semibold">New</span>
            </div>
          </>}
        </div>

        {/* Dropzone */}
        <div {...getRootProps()} className={`tw-w-full tw-rounded-lg tw-border-2 tw-border-dashed tw-cursor-pointer tw-transition-all tw-py-6 tw-flex tw-flex-col tw-items-center tw-gap-2 ${
          isDragActive ? 'tw-border-primary-400 tw-bg-primary-50' : 'tw-border-slate-200 tw-bg-slate-50 hover:tw-border-primary-300 hover:tw-bg-slate-100'
        }`}>
          <input {...getInputProps()} />
          <MdCloudUpload size={28} className={isDragActive ? 'tw-text-primary-500' : 'tw-text-slate-300'} />
          <p className="tw-text-sm tw-font-medium tw-text-slate-600 tw-mb-0">
            {isDragActive ? 'Drop here…' : 'Drop image or click to browse'}
          </p>
          <p className="tw-text-[11px] tw-text-slate-400 tw-mb-0">JPG · PNG · WEBP &nbsp;·&nbsp; Max 5 MB</p>
        </div>

        {/* File pill */}
        {selected && (
          <div className="tw-flex tw-items-center tw-gap-2 tw-w-full tw-px-3 tw-py-2 tw-bg-primary-50 tw-border tw-border-primary-100 tw-rounded">
            <span className="tw-flex-1 tw-text-xs tw-text-primary-700 tw-font-medium tw-truncate">{selected.file.name}</span>
            <span className="tw-text-[10px] tw-text-primary-400">{(selected.file.size / 1024).toFixed(0)} KB</span>
            <button type="button" onClick={() => setSelected(null)}
              className="tw-text-slate-400 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0 tw-transition-colors">
              <MdClose size={14} />
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
AvatarModal.propTypes = { show: PropTypes.bool, onHide: PropTypes.func, currentSrc: PropTypes.string, initials: PropTypes.string, onUpload: PropTypes.func, onRemove: PropTypes.func, uploading: PropTypes.bool, removing: PropTypes.bool };

// ── Profile ───────────────────────────────────────────────────────────────────
const Profile = () => {
  const dispatch = useDispatch();
  const { data: user } = useSelector(s => s.profile_user);
  const emp = user?.employee || {};

  const [showAvatar,   setShowAvatar]   = useState(false);
  const [showEdit,     setShowEdit]     = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [removing,     setRemoving]     = useState(false);
  const [liveAvatar,   setLiveAvatar]   = useState(null);

  const displayName = `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || user?.full_name || user?.name;
  const initials    = displayName ? displayName.split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase() : '?';
  const avatarSrc   = liveAvatar || user?.avatar_url;

  useEffect(() => { dispatch(getProfileUser()); }, [dispatch]);

  // ── Avatar ────────────────────────────────────────────────────────────────
  const handleAvatarUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      await dispatch(changeAvatar({ userId: user.id, formData: fd })).unwrap();
      setLiveAvatar(URL.createObjectURL(file));
      await dispatch(getProfileUser());
      setShowAvatar(false);
      toast.success('Avatar updated.');
    } catch {
      toast.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleAvatarRemove = () => {
    confirmAlert({
      title: 'Remove profile photo?',
      message: 'Your initials will be shown until you upload another photo or sign in with Google again.',
      closeOnEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          label: 'Remove photo',
          variant: 'danger',
          onClick: async () => {
            setRemoving(true);
            try {
              await dispatch(removeAvatar({ userId: user.id })).unwrap();
              setLiveAvatar(null);
              await dispatch(getProfileUser()).unwrap();
              setShowAvatar(false);
              toast.success('Profile photo removed.');
            } catch (error) {
              toast.error(error?.message || 'Failed to remove profile photo.');
            } finally {
              setRemoving(false);
            }
          },
        },
        { label: 'Keep photo' },
      ],
    });
  };

  // ── Edit form ─────────────────────────────────────────────────────────────
  const { register: rEdit, handleSubmit: sEdit, formState: { isSubmitting: eBusy } } = useForm({
    defaultValues: { first_name: emp.first_name, last_name: emp.last_name, phone: emp.phone },
  });
  const onEdit = async () => { toast.info('Update coming soon.'); setShowEdit(false); };

  return (
    <div className="tw-flex tw-flex-col tw-gap-5 tw-max-w-4xl tw-mx-auto">

      {/* ── HERO ── */}
      <div className="tw-rounded-2xl tw-overflow-hidden tw-shadow-sm tw-border tw-border-slate-100 tw-bg-white">
        {/* Cover */}
        <div className="tw-relative tw-h-36 sm:tw-h-44 tw-bg-gradient-to-br tw-from-primary-700 tw-via-blue-600 tw-to-sky-500">
          <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-primary-900/60 tw-via-black/20 tw-to-transparent" />
        </div>

        {/* Identity bar */}
        <div className="tw-px-6 tw-pb-5">
          <div className="tw-flex tw-items-end tw-justify-between tw-gap-4 -tw-mt-11">

            {/* Avatar — click → modal */}
            <div className="tw-relative tw-group tw-cursor-pointer tw-shrink-0"
              onClick={() => setShowAvatar(true)} title="Change avatar">
              <AvatarDisplay
                src={avatarSrc} initials={initials}
                className="tw-w-[88px] tw-h-[88px] tw-border-[3px] tw-border-white tw-shadow-md tw-text-2xl"
                imgClass="tw-w-[88px] tw-h-[88px]"
              />
              <div className="tw-absolute tw-inset-0 tw-rounded-xl tw-bg-black/0 group-hover:tw-bg-black/35 tw-transition-all tw-border-[3px] tw-border-white tw-flex tw-items-center tw-justify-center">
                <MdCameraAlt size={20} className="tw-text-white tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-drop-shadow-sm" />
              </div>
            </div>

            {/* Action buttons — aligned to bottom-right of cover */}
            <div className="tw-flex tw-gap-2 tw-pb-1">
              <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
                <MdEdit size={13} className="tw-mr-1" />Edit Profile
              </Button>
            </div>
          </div>

          {/* Name + meta */}
          <div className="tw-mt-3">
            <h2 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-0.5 tw-flex tw-items-center tw-gap-1.5">
              {displayName}
              <MdVerified size={17} className="tw-text-blue-500 tw-shrink-0" />
            </h2>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2.5 tw-gap-y-0.5 tw-text-[13px] tw-text-slate-500">
              {emp.title && <span>{emp.title}</span>}
              {emp.department && <><span className="tw-text-slate-200">·</span><span>{emp.department}</span></>}
              {emp.site && (
                <><span className="tw-text-slate-200">·</span>
                <span className="tw-flex tw-items-center tw-gap-0.5">
                  <MdLocationOn size={13} className="tw-text-slate-400" />{emp.site}
                </span></>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── INFO GRID ── */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[1fr_300px] tw-gap-5">

        {/* Contact & Info card */}
        <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-overflow-hidden">
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-100">
            <MdPerson size={16} className="tw-text-primary-400" />
            <span className="tw-text-sm tw-font-semibold tw-text-slate-700">Contact Information</span>
          </div>
          <div className="tw-px-5 tw-py-1">
            <InfoRow icon={MdEmail}     iconCls="tw-bg-primary-100 !tw-text-primary-500"  label="Email"      value={user?.email} />
            <InfoRow icon={MdPhone}     iconCls="tw-bg-emerald-100 !tw-text-emerald-600" label="Phone"      value={emp.phone} />
            <InfoRow icon={MdBusiness}  iconCls="tw-bg-amber-100 !tw-text-amber-600"    label="Department" value={emp.department} />
            <InfoRow icon={MdLocationOn}iconCls="tw-bg-rose-100 !tw-text-rose-500"      label="Site"       value={emp.site} />
          </div>
        </div>

        {/* Account & Security card */}
        <div className="tw-bg-white tw-rounded-2xl tw-border tw-border-slate-100 tw-shadow-sm tw-overflow-hidden">
          <div className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-3.5 tw-border-b tw-border-slate-100">
            <MdAccountCircle size={16} className="tw-text-primary-400" />
            <span className="tw-text-sm tw-font-semibold tw-text-slate-700">Account</span>
          </div>
          <div className="tw-px-5 tw-py-1">
            <InfoRow icon={MdBadge} iconCls="tw-bg-violet-100 !tw-text-violet-600" label="Username" value={user?.name} />
            <InfoRow icon={MdEmail} iconCls="tw-bg-sky-100 !tw-text-sky-600"       label="Full name" value={user?.full_name} />
          </div>
        </div>
      </div>

      <div className="tw-mt-5">
        <MySignInActivity />
      </div>

      {/* ── MODALS & DRAWER ── */}
      <AvatarModal
        show={showAvatar}
        onHide={() => setShowAvatar(false)}
        currentSrc={avatarSrc}
        initials={initials}
        onUpload={handleAvatarUpload}
        onRemove={handleAvatarRemove}
        uploading={uploading}
        removing={removing}
      />

      <Drawer
        show={showEdit}
        onHide={() => setShowEdit(false)}
        title={<span className="tw-flex tw-items-center tw-gap-1.5"><MdEdit size={14} />Edit Profile</span>}
        width="420px"
        footer={
          <div className="tw-flex tw-gap-2 tw-justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button size="sm" onClick={sEdit(onEdit)} disabled={eBusy}>{eBusy ? 'Saving…' : 'Save Changes'}</Button>
          </div>
        }
      >
        <div className="tw-p-5 tw-flex tw-flex-col tw-gap-4">
          {/* Mini avatar in drawer */}
          <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-slate-50 tw-rounded-lg tw-border tw-border-slate-100">
            <div className="tw-relative tw-cursor-pointer tw-group tw-shrink-0"
              onClick={() => { setShowEdit(false); setShowAvatar(true); }}>
              <AvatarDisplay src={avatarSrc} initials={initials}
                className="tw-w-12 tw-h-12 tw-border-2 tw-border-white tw-shadow-sm tw-text-base"
                imgClass="tw-w-12 tw-h-12" />
              <div className="tw-absolute tw-inset-0 tw-rounded-xl tw-bg-black/0 group-hover:tw-bg-black/30 tw-transition-all tw-border-2 tw-border-white tw-flex tw-items-center tw-justify-center">
                <MdCameraAlt size={14} className="tw-text-white tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity" />
              </div>
            </div>
            <div>
              <p className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-0">{displayName}</p>
              <button type="button"
                onClick={() => { setShowEdit(false); setShowAvatar(true); }}
                className="tw-text-[11px] tw-text-primary-500 hover:tw-underline tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-0">
                Change photo
              </button>
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <div><label className={LBL}>First Name</label><input className={INP} {...rEdit('first_name')} placeholder="First name" /></div>
            <div><label className={LBL}>Last Name</label><input className={INP} {...rEdit('last_name')} placeholder="Last name" /></div>
          </div>
          <div><label className={LBL}>Phone</label><input className={INP} {...rEdit('phone')} placeholder="Phone number" /></div>

          <div className="tw-pt-3 tw-border-t tw-border-slate-100 tw-flex tw-flex-col tw-gap-3">
            <p className="tw-text-[11px] tw-text-slate-400 tw-mb-0">Managed by HR — read only.</p>
            <div><label className={LBL}>Email</label><input className={INP_RO} readOnly value={user?.email || ''} /></div>
            <div className="tw-grid tw-grid-cols-2 tw-gap-3">
              <div><label className={LBL}>Title</label><input className={INP_RO} readOnly value={emp.title || ''} /></div>
              <div><label className={LBL}>Department</label><input className={INP_RO} readOnly value={emp.department || ''} /></div>
            </div>
            <div><label className={LBL}>Site</label><input className={INP_RO} readOnly value={emp.site || ''} /></div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default Profile;
